import { styled } from 'linaria/lib/react';
import { COLORS, FONTS } from '../../../../styles/constants';
import cardboardTexture from './assets/cardboard-texture.png';

// Animation timing constants
const CARD_ANIMATION_DURATION = '0.7s';
const CARD_ANIMATION_EASING = 'cubic-bezier(0, 0, 0, 1)';

// Main container
export const StyledSparkCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 10px;
`;

// Outer wrapper that clips scattered images at viewport bounds
// Uses negative margin + padding to extend the clipping area to match the parent's padding
// This allows scattered images to extend into the margin area but clips at viewport edge
export const StyledCardBodyClipWrapper = styled.div`
  position: relative;
  width: calc(100% + 48px);
  margin-left: -24px;
  padding: 0 24px;
  overflow: hidden;
`;

// Card body wrapper - maintains aspect ratio and contains both placeholder and animated content
export const StyledCardBodyWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 342 / 430;
  border-radius: 12px;
`;

// Card body with scattered images - overflow visible to show images extending beyond
// Includes card entrance animation: opacity, translateY, rotate, scale
export const StyledCardBody = styled.div<{ isCardAnimating?: boolean }>`
  position: absolute;
  inset: 0;
  border-radius: 10px;
  overflow: visible;

  /* Card entrance animation */
  opacity: ${({ isCardAnimating }) => (isCardAnimating ? 1 : 0)};
  transform: ${({ isCardAnimating }) =>
    isCardAnimating ? 'translateY(0) rotate(0deg) scale(1)' : 'translateY(80px) rotate(-15deg) scale(1.1)'};
  transition: opacity 0.2s ${CARD_ANIMATION_EASING}, transform ${CARD_ANIMATION_DURATION} ${CARD_ANIMATION_EASING};
`;

// Empty card placeholder - visible before animation
// Uses solid background to match spark cards in user profiles
export const StyledEmptyCard = styled.div<{ isHidden?: boolean; isDark?: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 0;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: linear-gradient(142deg, rgba(255, 255, 255, 0.2) 8.12%, rgba(255, 255, 255, 0.05) 94.24%);
  backdrop-filter: blur(20px);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ isHidden }) => (isHidden ? 0 : 1)};
  transition: opacity 0.3s ease-out;
  pointer-events: ${({ isHidden }) => (isHidden ? 'none' : 'auto')};
`;

export const StyledEmptyCardLabel = styled.div<{ isDark?: boolean }>`
  color: ${({ isDark }) => (isDark ? COLORS.white[60] : COLORS.brown.dark[40])};
  font: 600 14px/140% ${FONTS.secondary};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

// Inner container for card content - positioned ON TOP of scattered images
export const StyledCardInner = styled.div<{ bg?: string }>`
  position: absolute;
  inset: 0;
  z-index: 1;
  border-radius: 10px;
  overflow: hidden;
  background-color: ${COLORS.brown.dark[100]};
  background-image: ${({ bg }) => (bg ? `url(${bg})` : 'none')};
  background-position: center;
  background-size: cover;
`;

// Cardboard texture overlay - color dodge blend mode, fades out during animation
export const StyledCardboardTexture = styled.div<{ isVisible?: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 3;
  background-image: url(${cardboardTexture});
  background-size: cover;
  background-position: center;
  mix-blend-mode: color-dodge;
  pointer-events: none;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transition: opacity 0.3s ease-out;
  transition-delay: 0.2s;
`;

export const StyledCardVideo = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
`;

export const StyledGradientOverlay = styled.div<{ gradientStart?: string; gradientEnd?: string }>`
  position: absolute;
  inset: 0;
  background: ${({ gradientStart, gradientEnd }) =>
    gradientStart && gradientEnd ? `linear-gradient(135deg, ${gradientStart} 0%, ${gradientEnd} 100%)` : 'transparent'};
  z-index: 0;
`;

// Scattered images container - positioned BEHIND the cover image
export const StyledScatteredImages = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: visible;
`;

// Individual scattered image - shift and rotation are part of animation
// Using CSS custom properties for dynamic animation values
export const StyledScatteredImage = styled.div<{
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  width: number | null;
  height: number | null;
  zIndex: number;
  delay: number;
  isVisible: boolean;
  rotation: number; // Rotation in degrees
  shiftX: number; // Computed shift (already includes direction)
  isLeftSide: boolean;
}>`
  position: absolute;
  top: ${({ top }) => top || 'auto'};
  bottom: ${({ bottom }) => bottom || 'auto'};
  left: ${({ left }) => left || 'auto'};
  right: ${({ right }) => right || 'auto'};
  width: ${({ width }) => (width !== null ? `${width}px` : 'auto')};
  height: ${({ height }) => (height !== null ? `${height}px` : 'auto')};
  z-index: ${({ zIndex }) => zIndex};

  /* Set CSS custom properties for animation target values */
  --target-shift: ${({ shiftX, isLeftSide }) => (isLeftSide ? shiftX : -shiftX)}px;
  --target-rotation: ${({ rotation }) => rotation}deg;

  /* Orbit offsets — updated per-frame via direct DOM mutation (requestAnimationFrame) */
  --orbit-x: 0px;
  --orbit-y: 0px;
  --orbit-rot: 0deg;

  /* Transition for smooth entrance animation — removed once orbit starts */
  transition: opacity 0.5s ease-out, transform 0.5s ease-out;
  transition-delay: ${({ delay }) => delay}ms;

  /* When orbit is active, disable transition so lottie-driven updates are instant */
  &.orbit-active {
    transition: opacity 0.5s ease-out;
  }

  /* Hidden state */
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transform: ${({ isVisible }) =>
    isVisible
      ? 'translateY(-50%) translateX(calc(var(--target-shift) + var(--orbit-x))) translateY(var(--orbit-y)) scale(1) rotate(calc(var(--target-rotation) + var(--orbit-rot)))'
      : 'translateY(-50%) translateX(0px) scale(0.8) rotate(0deg)'};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 4px;
  }
`;

// Footer with action buttons - with fade-in animation
export const StyledFooter = styled.div<{ isAnimating?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 8px 8px 8px 12px;
  min-height: 36px;
  opacity: ${({ isAnimating }) => (isAnimating ? 1 : 0)};
  transition: opacity 0.4s ease-out;
`;

export const StyledFooterButtons = styled.div`
  display: flex;
  gap: 2px;
`;

export const StyledActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 6px;
  border: none;
  border-radius: 22px;
  background: transparent;
  cursor: pointer;
  color: ${COLORS.white[100]};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${COLORS.white[10]};
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

// Empty state
export const StyledEmptyState = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${COLORS.white[60]};
  font: 400 16px/140% ${FONTS.secondary};
  text-align: center;
  padding: 24px;
`;

// Noise/grain overlay
export const StyledNoiseOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
  opacity: 0.05;
  pointer-events: none;
  z-index: 2;
`;
